/**
 * DocUploadSlot — a single uploadable document row used in DocumentUpload
 * and ProviderWorkspace. Handles the file-picker interaction locally,
 * reports the chosen file via onFileSelected.
 *
 * Three visual states:
 *   idle      – dashed border, "Browse file" prompt
 *   staged    – file chosen, not yet uploaded (amber border)
 *   uploaded  – server confirmed (forest border + checkmark)
 *   error     – upload failed (destructive border)
 */
import * as React from 'react';
import { FileText, CheckCircle2, Upload, Loader2, X } from 'lucide-react';
import { validateDocumentFile } from '../utils';
import { getDocLabel } from '../utils';
import { ACCEPT_ATTR } from '../constants';
import { toast } from '@finmin/ui';

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

export type SlotState = 'idle' | 'staged' | 'uploading' | 'uploaded' | 'error';

interface Props {
  documentType: string;
  /** Progress 0-100 when uploading */
  progress?: number;
  state?: SlotState;
  /** Filename to display when staged or uploaded */
  filename?: string;
  onFileSelected: (file: File, documentType: string) => void;
  onRemove?: () => void;
  /** Show remove button even when uploaded */
  allowRemove?: boolean;
}

export function DocUploadSlot({
  documentType,
  progress = 0,
  state = 'idle',
  filename,
  onFileSelected,
  onRemove,
  allowRemove = false,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const label    = getDocLabel(documentType);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be re-selected after removal
    e.target.value = '';
    const err = validateDocumentFile(file);
    if (err) { toast.error(err); return; }
    onFileSelected(file, documentType);
  }

  const borderColor =
    state === 'uploaded' ? FOREST :
    state === 'staged'   ? AMBER  :
    state === 'error'    ? 'hsl(var(--destructive))' :
    'hsl(var(--border))';

  const bgColor =
    state === 'uploaded' ? `${FOREST}0a` :
    state === 'staged'   ? `${AMBER}0a`  :
    'transparent';

  return (
    <div
      className="rounded-xl border p-3 transition-all"
      style={{ borderColor, backgroundColor: bgColor }}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: state === 'uploaded' ? `${FOREST}15` : 'hsl(var(--muted))' }}
        >
          {state === 'uploading'
            ? <Loader2 className="h-4.5 w-4.5 animate-spin" style={{ color: FOREST }} aria-hidden />
            : state === 'uploaded'
            ? <CheckCircle2 className="h-4.5 w-4.5" style={{ color: FOREST }} aria-hidden />
            : <FileText className="h-4.5 w-4.5 text-muted-foreground" aria-hidden />
          }
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground truncate">{label}</p>
          {filename && (
            <p className="text-[10px] text-muted-foreground truncate mt-0.5 italic">
              {filename}
            </p>
          )}
          {state === 'uploading' && (
            <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${progress}%`, backgroundColor: FOREST }}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {(state === 'staged' || state === 'error' || (state === 'uploaded' && allowRemove)) && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Remove file"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {state !== 'uploaded' && state !== 'uploading' && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="h-7 px-2.5 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-colors"
              style={{ backgroundColor: MINT, color: FOREST }}
            >
              <Upload className="h-3 w-3" aria-hidden />
              {state === 'staged' ? 'Replace' : 'Browse'}
            </button>
          )}
          {state === 'uploaded' && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="h-7 px-2 rounded-lg text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Replace
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="sr-only"
        onChange={handleChange}
        aria-label={`Upload ${label}`}
      />
    </div>
  );
}
